import { applyInventoryFilters } from "./filterUtils";
import { useBondCharacteristicStore } from "../Store/bondCharacteristicStore";
import { useActualOrderStore } from "../Store/actualOrderStore";
import { useBondLadderRungFlowStore } from "../Store/bondLadderRungFlowStore";
import { processBondData } from "../Component/OrderComponents/utils";
import { useSelectedLadderOrdersStore } from "../Store/selectedLadderOrdersStore";
import { convertMaturityToYears } from "./filterUtils"; // Import the utility
import { filterObjectByBondType } from "./bondTypeFieldFilter";

/**
 * Handles navigation to inventory or bond ladder details based on active tab
 * 
 * @param {number} activeTab - Current active tab (1 for bond characteristic, 2 for bond ladder)
 * @param {object} bondCharacteristicData - Bond characteristic data from the store
 * @param {function} setCurrentCharacteristicID - Function to set current characteristic ID
 * @param {function} setActualOrderData - Function to update actual order data
 * @param {function} navigate - React router navigate function
 * @param {function} showError - Function to display error messages
 * @param {function} setLoading - Function to update loading state
 * @param {boolean} clearAllOrders - Flag to determine if all orders should be cleared (default: true)
 * @returns {void}
 */
/**
 * Flattens a characteristic row and navigates to inventory for it.
 *
 * This is the whole "view inventory for a characteristic" sequence in one place so the
 * Bond Characteristic tab and the ladder's View Characteristics action run identical
 * code rather than two copies that can drift.
 *
 * @param {object} characteristicRow - raw characteristic row (unflattened)
 * @param {object} options - { navigate, showError, setLoading, fallbackAccount }
 */
export const openInventoryForCharacteristic = (
  characteristicRow,
  { navigate, showError, setLoading, fallbackAccount } = {}
) => {
  const flattened = processBondData(characteristicRow);
  // A rung's characteristic is placed on the ladder's account. When the characteristic row
  // does not carry one, fall back to the ladder's so the order that follows lands on the
  // account it was placed from rather than the user's first account.
  if (!flattened.account && fallbackAccount) {
    flattened.account = fallbackAccount;
  }
  const characteristicStore = useBondCharacteristicStore.getState();

  characteristicStore.resetBondCharacteristicData();
  characteristicStore.setBondCharacteristicData(flattened);

  // handleViewInventory stamps the ladder/rung context from the row it is given
  return handleViewInventory(
    1,
    flattened,
    characteristicStore.setCurrentCharacteristicID,
    useActualOrderStore.getState().setActualOrderData,
    navigate,
    showError,
    setLoading
  );
};

/**
 * The inventory search is no longer the characteristic's — a filter was crossed out or
 * changed — so an order raised from it is a plain order. Drops the characteristic and, when
 * it was a rung's, its ladder linkage. Cleared from the order as well as from the
 * characteristic context, so ids stamped before the filters were touched cannot ride along
 * on a submission. Editing the characteristic itself is the other case: that flow
 * re-establishes all three and is meant to keep them.
 */
export const clearCharacteristicLinkage = () => {
  useBondCharacteristicStore.getState().resetBondCharacteristicId();
  useActualOrderStore.getState().setActualOrderData({
    characteristicId: null,
    bondLadderId: null,
    bondLadderRungId: null,
  });
};

export const handleViewInventory = (
  activeTab,
  bondCharacteristicData,
  setCurrentCharacteristicID,
  setActualOrderData,
  navigate,
  showError,
  setLoading,
  clearAllOrders = true,
) => {
  if (activeTab === 1) {
    const {
      bondType,
      side,
      callable,
      maturityMin,
      maturityMax,
      priceMin,
      priceMax,
      yieldMin,
      yieldMax,
      couponMin,
      couponMax,
      state,
      minimumRating,
      insured,
      taxable,
      paymentFrequency,
      incomeSource,
      altMinTax,
      securityCharge,
      sector,
      nextCallDate,
      zeroCoupon,
      characteristicId,
      issuer,
    } = bondCharacteristicData;
    
    // A characteristic may store either the UI spelling (maturityMin, usually "5Y") or the
    // API spelling with absolute dates (minMaturity: "2027-03-10"). Accept both and pass the
    // value through untouched, so an exact date stays an exact date.
    const effectiveMaturityMin = maturityMin ?? bondCharacteristicData.minMaturity;
    const effectiveMaturityMax = maturityMax ?? bondCharacteristicData.maxMaturity;

    setCurrentCharacteristicID(characteristicId);
    // Remember whether this characteristic belongs to a ladder rung so the order
    // placed from inventory can be stamped with the ladder + rung ids.
    useBondCharacteristicStore
      .getState()
      .setCharacteristicLadderContext(bondCharacteristicData);
    // The order placed from this inventory view belongs on the characteristic's account.
    // Written unconditionally: an absent account must clear a previously viewed order's
    // leftover rather than silently keep it.
    setActualOrderData({
      name: bondCharacteristicData.userName,
      account: bondCharacteristicData.account ?? "",
    });

    let type;
    if (bondType === "Treasury") {
      type = "Gov";
    } else if (bondType === "Corporates") {
      type = "Corp";
    } else if (bondType === "Municipals") {
      type = "Muni";
    } else {
      showError("Invalid bond type.");
      return;
    }

    // Handle maturity values consistently using the utility
    const filters = {
      type,
      side,
      ...(effectiveMaturityMin && { maturityMin: effectiveMaturityMin }),
      ...(effectiveMaturityMax && { maturityMax: effectiveMaturityMax }),
      ...(nextCallDate && { nextCallDate }),
      ...(bondCharacteristicData.duration && {
        duration: bondCharacteristicData.duration,
      }),
      ...(priceMin !== undefined &&
        priceMin !== "" && { minPrice: Number(priceMin) }),
      ...(priceMax !== undefined &&
        priceMax !== "" && { maxPrice: Number(priceMax) }),
      ...(yieldMin !== undefined &&
        yieldMin !== "" && { minYtm: Number(yieldMin) }),
      ...(yieldMax !== undefined &&
        yieldMax !== "" && { maxYtm: Number(yieldMax) }),
      ...(couponMin !== undefined &&
        couponMin !== "" && { minCoupon: Number(couponMin) }),
      ...(couponMax !== undefined &&
        couponMax !== "" && { maxCoupon: Number(couponMax) }),
      ...(insured && { insured }),
      ...(taxable && { taxable }),
      ...(altMinTax && { altMinTax }),
      ...(zeroCoupon && { zeroCoupon }),
      ...(state && { state }),
      ...(paymentFrequency && { paymentFrequency }),
      ...(sector && { sector }),
      ...(incomeSource && { incomeSource }),
      ...(callable && { callable }),
      ...(issuer && { issuer }),
      ...(securityCharge && { securityCharge }),
      ...(minimumRating && {
        minRatingSP: minimumRating,
        maxRatingSP: "AAA",
      }),
      ...(bondCharacteristicData.minimumRatingMoody && {
        minRatingMoody: bondCharacteristicData.minimumRatingMoody,
        maxRatingMoody: "Aaa",
      }),
    };

    // NEW: remove unsupported filter fields based on original UI bondType
    const cleanedFilters = filterObjectByBondType(bondCharacteristicData.bondType, filters);

    // add source for bond characteristic view so inventory keeps the origin
    cleanedFilters.source = "viewInventoryBondChar";
    
    if (typeof setLoading === "function") setLoading(true);
    try {
      const payload = applyInventoryFilters(cleanedFilters, navigate, "bondchar");
      return payload;
    } catch (err) {
      if (typeof showError === "function") {
        showError("Failed to navigate to inventory.");
      }
      console.error(err);
    }
    if (typeof setLoading === "function") setLoading(false);
  } else if (activeTab === 2) {
    const ladderData = useBondLadderRungFlowStore.getState().ladderData;
    if (!ladderData) {
      if (typeof showError === "function") {
        showError("No ladder data available");
      }
      return;
    }

    if(clearAllOrders) {
      useSelectedLadderOrdersStore.getState().clearAllOrders();
    }
    const filters = {
      side: ladderData.side,
      ...(ladderData.bondType && { bondType: ladderData.bondType }),
      ...(ladderData.nextCallDate && {
        nextCallDate: ladderData.nextCallDate,
      }),
      ...(ladderData.duration && { duration: ladderData.duration }),
      ...(ladderData.priceMin !== undefined &&
        ladderData.priceMin !== "" && {
          minPrice: Number(ladderData.priceMin),
        }),
      ...(ladderData.priceMax !== undefined &&
        ladderData.priceMax !== "" && {
          maxPrice: Number(ladderData.priceMax),
        }),
      ...(ladderData.yieldMin !== undefined &&
        ladderData.yieldMin !== "" && {
          minYtm: Number(ladderData.yieldMin),
        }),
      ...(ladderData.yieldMax !== undefined &&
        ladderData.yieldMax !== "" && {
          maxYtm: Number(ladderData.yieldMax),
        }),
      ...(ladderData.couponMin !== undefined &&
        ladderData.couponMin !== "" && {
          minCoupon: Number(ladderData.couponMin),
        }),
      ...(ladderData.couponMax !== undefined &&
        ladderData.couponMax !== "" && {
          maxCoupon: Number(ladderData.couponMax),
        }),
      ...(ladderData.insured && { insured: ladderData.insured }),
      ...(ladderData.taxable && { taxable: ladderData.taxable }),
      ...(ladderData.altMinTax && { altMinTax: ladderData.altMinTax }),
      ...(ladderData.zeroCoupon && { zeroCoupon: ladderData.zeroCoupon }),
      ...(ladderData.state && { state: ladderData.state }),
      ...(ladderData.paymentFrequency && {
        paymentFrequency: ladderData.paymentFrequency,
      }),
      ...(ladderData.sector && { sector: ladderData.sector }),
      ...(ladderData.incomeSource && {
        incomeSource: ladderData.incomeSource,
      }),
      ...(ladderData.callable && { callable: ladderData.callable }),
      ...(ladderData.issuer && { issuer: ladderData.issuer }),
      ...(ladderData.securityCharge && { securityCharge: ladderData.securityCharge }),
      ...(ladderData.minimumRating && {
        minRatingSP: ladderData.minimumRating,
        maxRatingSP: "AAA",
      }),
      ...(ladderData.minimumRatingMoody && {
        minRatingMoody: ladderData.minimumRatingMoody,
        maxRatingMoody: "Aaa",
      }),
    };

    // NEW: filter unsupported fields for ladder bond type
    const cleanedFilters = filterObjectByBondType(ladderData.bondType, filters);

    // add source for ladder view so bond-ladder-details keeps the origin
    cleanedFilters.source = "viewInventoryLadder";

    if (typeof setLoading === "function") setLoading(true);
    try {
      const payload = applyInventoryFilters(cleanedFilters, navigate, "bondladder");
      return payload;
    } catch (err) {
      if (typeof showError === "function") {
        showError("Failed to navigate to inventory.");
      }
      console.error(err);
    }
    if (typeof setLoading === "function") setLoading(false);
  }
};
